import { forwardRef, type CSSProperties, type ReactNode } from "react";

import {
  COMMISSION_PER_SALE,
  OFFER_PRICE,
  PRICE_AFTER,
  type DailyBrief,
} from "./dailyBrief";

export const CARD_WIDTH = 1080;
export const CARD_HEIGHT = 1350;

const NAVY = "#0b1f3a";
const LIME = "#c6f36b";
const INK = "#0a0a0a";
const GREY = "#6b7280";

const box = (style: CSSProperties): CSSProperties => ({
  borderRadius: 40,
  padding: "30px 40px",
  ...style,
});

const Stat = ({ value, label }: { value: number; label: string }) => (
  <div
    style={{
      flex: 1,
      borderRadius: 28,
      background: "rgba(255,255,255,0.1)",
      padding: "14px 18px",
      display: "flex",
      flexDirection: "column",
      gap: 4,
    }}
  >
    <span style={{ fontSize: 44, fontWeight: 800, lineHeight: 1 }}>
      {value}
    </span>
    <span style={{ fontSize: 20, opacity: 0.8, whiteSpace: "nowrap" }}>
      {label}
    </span>
  </div>
);

const Small = ({ children }: { children: ReactNode }) => (
  <span
    style={{
      fontSize: 22,
      fontWeight: 700,
      letterSpacing: 3,
      textTransform: "uppercase",
    }}
  >
    {children}
  </span>
);

/**
 * The morning card for the setters' WhatsApp group (1080×1350, 4:5). Plain
 * inline styles so it turns into the same picture on every device.
 */
export const BriefCard = forwardRef<HTMLDivElement, { brief: DailyBrief }>(
  ({ brief }, ref) => {
    const medals = ["1º", "2º", "3º"];
    return (
      <div
        ref={ref}
        style={{
          width: CARD_WIDTH,
          height: CARD_HEIGHT,
          background: "#f4f5f7",
          color: INK,
          padding: 56,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 22,
          fontFamily:
            "Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <span
            style={{
              fontSize: 76,
              fontWeight: 900,
              letterSpacing: -3,
              color: NAVY,
              lineHeight: 0.9,
            }}
          >
            gula.
          </span>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 6,
              color: GREY,
            }}
          >
            <Small>Parte del día</Small>
            <span style={{ fontSize: 30, fontWeight: 600, color: INK }}>
              {brief.dateLabel}
            </span>
          </div>
        </div>

        {/* Black Friday */}
        {brief.blackFridayDaysLeft ? (
          <div
            style={box({
              background: INK,
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 24,
            })}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Small>Black Friday</Small>
              <span style={{ fontSize: 30, lineHeight: 1.25, opacity: 0.9 }}>
                {OFFER_PRICE} € con instalación incluida.
                <br />
                El 1 de diciembre sube a {PRICE_AFTER} €.
              </span>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                color: LIME,
                minWidth: 200,
              }}
            >
              <span style={{ fontSize: 96, fontWeight: 900, lineHeight: 1 }}>
                {brief.blackFridayDaysLeft}
              </span>
              <span style={{ fontSize: 26, fontWeight: 700 }}>
                {brief.blackFridayDaysLeft === 1 ? "último día" : "días quedan"}
              </span>
            </div>
          </div>
        ) : null}

        {/* Today's work */}
        <div
          style={box({
            background: NAVY,
            color: "white",
            display: "flex",
            flexDirection: "column",
            gap: 18,
          })}
        >
          <div style={{ display: "flex", alignItems: "baseline", gap: 20 }}>
            <span
              style={{
                fontSize: 124,
                fontWeight: 900,
                lineHeight: 0.9,
                color: LIME,
              }}
            >
              {brief.waiting}
            </span>
            <span style={{ fontSize: 42, fontWeight: 700, lineHeight: 1.1 }}>
              leads te esperan hoy
            </span>
          </div>
          <div style={{ display: "flex", gap: 14 }}>
            <Stat value={brief.hot} label="calientes" />
            <Stat value={brief.recontactsToday} label="recontactos hoy" />
            <Stat value={brief.sureSales} label="en cierre de compra" />
            <Stat value={brief.newYesterday} label="nuevos ayer" />
          </div>
        </div>

        {/* Race of the month */}
        <div
          style={box({
            background: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 24,
          })}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ color: GREY }}>
              <Small>La carrera del mes</Small>
            </span>
            {brief.top.length ? (
              brief.top.map((row, index) => (
                <span
                  key={`${row.first_name}-${index}`}
                  style={{ fontSize: 30, fontWeight: index ? 600 : 800 }}
                >
                  {medals[index]} {row.first_name}
                  <span style={{ color: GREY, fontWeight: 500 }}>
                    {" "}
                    · {row.points} pts
                  </span>
                </span>
              ))
            ) : (
              <span style={{ fontSize: 34, fontWeight: 700 }}>
                Nadie ha puntuado aún: ¡sé el primero!
              </span>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              minWidth: 220,
            }}
          >
            <span
              style={{
                fontSize: 84,
                fontWeight: 900,
                lineHeight: 1,
                color: NAVY,
              }}
            >
              {brief.monthSales}
            </span>
            <span style={{ fontSize: 24, color: GREY, textAlign: "center" }}>
              ventas del equipo
              <br />
              este mes
            </span>
          </div>
        </div>

        {/* Line of the day */}
        <div
          style={box({
            background: LIME,
            flex: 1,
            display: "flex",
            alignItems: "center",
          })}
        >
          <span
            style={{
              fontSize: 48,
              fontWeight: 800,
              lineHeight: 1.2,
              letterSpacing: -0.5,
            }}
          >
            {brief.line}
          </span>
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 26,
            fontWeight: 700,
          }}
        >
          <span>Conéctate y a llamar · crm.guladeagua.es</span>
          <span
            style={{
              background: INK,
              color: LIME,
              borderRadius: 999,
              padding: "10px 24px",
            }}
          >
            {COMMISSION_PER_SALE} € por venta
          </span>
        </div>
      </div>
    );
  },
);
BriefCard.displayName = "BriefCard";
